import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Center, Checkbox, Code, Collapse, CopyButton, Group, Loader, Modal,
  NumberInput, Select, Stack, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle, IconCheck, IconChevronDown, IconChevronUp, IconCopy, IconFileText,
  IconInfoCircle, IconSend, IconSettings,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PhoneInput } from './PhoneInput';

interface ParentPhone { id: string; label: string; number: string; isPrimary: boolean }
interface StudentContractInfo {
  id: string; fullName: string; address?: string;
  parentName?: string; parentPhone?: string;
  parent?: { id: string; fullName: string; phone?: string; phones?: ParentPhone[] } | null;
  tuitionPlan?: { monthlyAmount: number; discount: number } | null;
}
interface ContractResult {
  /** Yaratilgan shartnoma `id`si — natija ekranidagi "Shartnomani ochish" havolasi uchun. */
  id: string;
  contractNumber: string;
  token: string;
  link: string;
  /** Ilovadagi ota-onaga bildirishnoma ketdimi (SMS yo'q). `false` — akkaunt bog'lanmagan. */
  notified: boolean;
  /** Ota-onaga yangi raqam biriktirish natijasi (jimgina yutilmaydi). */
  phoneAttached?: 'ok' | 'failed' | null;
}

/**
 * Rekvizitlarni KIM to'ldira oladi — backend `/contracts/requisites` STAFF talab qiladi
 * (`middleware/authorize.STAFF`), `Settings.tsx` ham bo'limni AYNAN shu ro'yxat bo'yicha
 * chizadi. RECEPTION/ACCOUNTANT Sozlamalarni ocha oladi, lekin rekvizitlar bo'limini
 * KO'RMAYDI — ularga "Sozlamalarga o'ting" deyish boshi berk ko'cha bo'lardi.
 */
const REQUISITES_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN'];

/** Server chegarasi (`routes/contracts.ts` MAX_MONTHLY_PAYMENT) bilan bir xil. */
const MAX_MONTHLY_PAYMENT = 100_000_000;

/** Serverdagi `latinizePassport` bilan bir xil ko'rinishdosh kirill harflari. */
const CYRILLIC_LOOKALIKE: Record<string, string> = {
  А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H', О: 'O',
  Р: 'P', С: 'C', Т: 'T', Х: 'X', У: 'Y', І: 'I', Ј: 'J', Ѕ: 'S',
};

/**
 * Pasportni "AB 1234567" ko'rinishiga keltiradi va kirill "ko'rinishdosh" harflarni
 * lotinga o'giradi — server (`contractSign.latinizePassport`) bilan AYNAN bir xil qoida,
 * shuning uchun admin ko'rgan qiymat serverda boshqacha tekshirilmaydi.
 */
export function formatPassport(v: string): string {
  const up = v.toUpperCase().replace(/[Ѐ-ӿ]/g, (ch) => CYRILLIC_LOOKALIKE[ch] ?? ch);
  const s = up.replace(/[^A-Z0-9]/g, '').slice(0, 9);
  const letters = s.slice(0, 2).replace(/[^A-Z]/g, '');
  const digits = s.slice(letters.length).replace(/\D/g, '').slice(0, 7);
  if (!letters) return digits;
  return digits ? `${letters} ${digits}` : letters;
}

/** Server sanani "YYYY-MM-DD" ko'rinishida TOSHKENT kuni deb talqin qiladi (lib/dates.tzDateTime). */
const wallDate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

/** Serverdagi `txt()` bilan bir xil: ketma-ket bo'shliqlar bittaga tushadi. */
const squash = (v: string) => v.replace(/\s+/g, ' ').trim();

const digitsOf = (x?: string) => (x || '').replace(/\D/g, '');

/**
 * O'quvchi qatorida "Shartnoma" tugmasi — shartnoma yaratib, ota-onaga ILOVA orqali
 * yuboradi (SMS yo'q). Ota-ona akkaunti bog'lanmagan bo'lsa, xodim havolani o'zi ulashadi.
 * `variant='icon'` — jadval qatori uchun (standart), `variant='button'` — o'quvchi
 * kartochkasidagi "Shartnomalar" tabi uchun matnli tugma.
 *
 * RAQAM: mijozda generatsiya QILINMAYDI. Avval `SATA-${yil}-${id.slice(-5)}` deterministik
 * qiymat yuborilardi — u tenant prefiksini hisobga olmasdi va ikkinchi shartnomada
 * DUBLIKAT berardi. Endi raqamni server beradi (`<PREFIX>-<yil>-<0001>`), foydalanuvchi
 * xohlasa o'z raqamini kiritishi mumkin (band bo'lsa server 409 qaytaradi).
 */
export function ContractButton({ studentId, variant = 'icon' }: { studentId: string; variant?: 'icon' | 'button' }) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  /**
   * T1: shaxsni tasdiqlovchi maydonlar (manzil / pasport / JSHSHIR) YOPIQ turadi.
   * Ularni ota-ona havolada O'ZI to'ldiradi va imzolashda tasdiqlaydi — maktabga
   * ularni oldindan yig'ish shart emas (server ham ixtiyoriy deb qabul qiladi).
   * Butunlay olib tashlanmadi: qog'oz shartnomani oldindan chop etish kabi
   * holatlarda admin baribir kirita olishi kerak.
   */
  const [identityOpen, identityCtl] = useDisclosure(false);
  const [result, setResult] = useState<ContractResult | null>(null);
  /** Rekvizitlar to'ldirilmagani sababli kelgan 400 — Sozlamalarga havola bilan ko'rsatiladi. */
  const [requisitesError, setRequisitesError] = useState<string | null>(null);
  const qc = useQueryClient();
  const { user } = useAuth();
  /** Rekvizitlarni o'zi to'g'rilay oladimi — yo'q bo'lsa havola o'rniga "ma'murga murojaat qiling". */
  const canFixRequisites = REQUISITES_ROLES.includes(user?.role ?? '');

  const { data: s, isLoading } = useQuery({
    queryKey: ['student-contract', studentId],
    queryFn: async () => (await api.get<StudentContractInfo>(`/students/${studentId}`)).data,
    enabled: opened,
  });

  const now = new Date();
  const form = useForm({
    initialValues: {
      contractNumber: '',
      date: now as Date,
      parentFullName: '',
      studentFullName: '',
      parentAddress: '',
      monthlyPayment: 0 as number | string,
      monthsCount: 10 as number | string,
      passport: '',
      jshshir: '',
      parentPhone: '',
      fromDate: new Date(now.getFullYear(), 8, 1) as Date | null,
      toDate: new Date(now.getFullYear() + 1, 4, 31) as Date | null,
      /** Yangi raqam ota-onaga biriktirilsinmi (avval JIMGINA bajarilardi). */
      attachPhone: true,
    },
    // Qoidalar SERVER bilan AYNAN bir xil (`routes/contracts.ts` parseInput +
    // `lib/contractSign.ts` validateParentInput). Avval admin faqat "bo'sh emas"ni
    // tekshirar, ota-ona sahifasi esa formatni — nomuvofiqlik 400 bilan qaytardi.
    validate: {
      // F.I.O., manzil va pasport MAKTAB uchun ixtiyoriy (ota-ona o'zi to'ldiradi), lekin
      // kiritilgan bo'lsa server AYNI qoida bilan tekshiradi.
      parentFullName: (v) => (!squash(v) || squash(v).length >= 5 ? null : t('contractGen.parentNameTooShort')),
      parentAddress: (v) => (!squash(v) || squash(v).length >= 5 ? null : t('contractGen.addressTooShort')),
      passport: (v) => (!squash(v) || /^[A-Z]{2}\s?\d{7}$/.test(squash(v)) ? null : t('contractGen.passportInvalid')),
      jshshir: (v) => (!digitsOf(v) || digitsOf(v).length === 14 ? null : t('contractGen.jshshirInvalid')),
      parentPhone: (v) => (digitsOf(v).length === 12 ? null : t('contractGen.parentPhoneInvalid')),
      monthlyPayment: (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n <= 0) return t('contractGen.paymentRequired');
        return n > MAX_MONTHLY_PAYMENT ? t('contractGen.paymentTooBig') : null;
      },
      monthsCount: (v) => {
        const n = Number(v);
        return Number.isInteger(n) && n >= 1 && n <= 12 ? null : t('contractGen.monthsInvalid');
      },
      fromDate: (v) => (v ? null : t('contractGen.fromDateRequired')),
      toDate: (v, values) => {
        if (!v) return t('contractGen.toDateRequired');
        return values.fromDate && v.getTime() <= values.fromDate.getTime() ? t('contractGen.toDateBeforeFrom') : null;
      },
    },
  });

  useEffect(() => {
    if (s) {
      form.setValues({
        studentFullName: s.fullName,
        parentFullName: s.parent?.fullName || s.parentName || '',
        parentPhone: s.parent?.phones?.find((p) => p.isPrimary)?.number || s.parent?.phones?.[0]?.number || s.parent?.phone || s.parentPhone || '',
        parentAddress: s.address || '',
        monthlyPayment: s.tuitionPlan ? s.tuitionPlan.monthlyAmount - s.tuitionPlan.discount : 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  const knownPhones = s?.parent?.phones ?? [];
  /** Kiritilgan raqam ota-onaning ma'lum raqamlari orasida yo'q — biriktirish taklif qilinadi. */
  const isNewPhone =
    !!s?.parent?.id &&
    digitsOf(form.values.parentPhone).length === 12 &&
    !knownPhones.some((p) => digitsOf(p.number) === digitsOf(form.values.parentPhone));

  const sendMut = useMutation({
    mutationFn: async (v: typeof form.values) => {
      // TARTIB MUHIM: AVVAL shartnoma yaratiladi, KEYIN raqam biriktiriladi.
      // Avval teskari edi: yaratish yiqilsa ham (rekvizit yo'q, raqam band, validatsiya)
      // ota-onaning kontaktlarida yangi raqam qolib ketardi va uni faqat qo'lda
      // o'chirish mumkin bo'lardi.
      const res = await api.post<ContractResult>('/contracts', {
        studentId,
        // Bo'sh bo'lsa YUBORILMAYDI — raqamni server generatsiya qiladi
        contractNumber: squash(v.contractNumber) || undefined,
        date: wallDate(v.date),
        parentAddress: squash(v.parentAddress),
        parentFullName: squash(v.parentFullName),
        studentFullName: squash(v.studentFullName),
        monthlyPayment: Number(v.monthlyPayment),
        monthsCount: Number(v.monthsCount),
        passport: squash(v.passport),
        jshshir: digitsOf(v.jshshir),
        parentPhone: v.parentPhone,
        fromDate: wallDate(v.fromDate),
        toDate: wallDate(v.toDate),
      });

      // Yangi raqamni biriktirish JIMGINA emas: foydalanuvchi belgilaydi va natija
      // (muvaffaqiyat/xato) yakuniy ekranda ko'rsatiladi. Bu yerdagi xato shartnomani
      // BEKOR QILMAYDI — shartnoma allaqachon yaratilgan va ota-onaga yuborilgan.
      let phoneAttached: ContractResult['phoneAttached'] = null;
      if (isNewPhone && v.attachPhone && s?.parent?.id) {
        try {
          await api.post('/phones', { userId: s.parent.id, label: t('contractGen.phoneLabelExtra'), number: v.parentPhone });
          phoneAttached = 'ok';
          qc.invalidateQueries({ queryKey: ['student-contract', studentId] });
        } catch {
          phoneAttached = 'failed';
        }
      }
      return { ...res.data, phoneAttached };
    },
    onSuccess: (data) => {
      setResult(data);
      setRequisitesError(null);
      // O'quvchi kartochkasidagi tab ham, /contracts sahifasi ham yangilansin
      qc.invalidateQueries({ queryKey: ['contracts'] });
      notifications.show({
        color: data.notified ? 'green' : 'yellow',
        message: data.notified ? t('contractGen.createdNotified') : t('contractGen.createdNoApp'),
      });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { status?: number; data?: { error?: string } } };
      const msg = err.response?.data?.error;
      const status = err.response?.status;
      if (status === 409) {
        // Raqam band — aynan shu maydonda ko'rsatamiz (server xabari aniq)
        form.setFieldError('contractNumber', msg ?? t('contractGen.numberTaken'));
        notifications.show({ color: 'red', message: msg ?? t('contractGen.numberTaken') });
        return;
      }
      if (status === 400 && msg && /rekvizit/i.test(msg)) {
        setRequisitesError(msg);
        return;
      }
      notifications.show({ color: 'red', message: msg ?? t('common.error') });
    },
  });

  /**
   * Yopiq bo'limda NIMADIR to'ldirilgan bo'lsa — tugmadagi nishonda ko'rinadi
   * (masalan o'quvchi kartochkasidan manzil oldindan tortilgan). Aks holda admin
   * o'zi ko'rmagan qiymat jimgina yuborilardi.
   */
  const identityFilled = [
    squash(form.values.parentAddress),
    squash(form.values.passport),
    digitsOf(form.values.jshshir),
  ].filter(Boolean).length;

  const closeAll = () => {
    setResult(null);
    setRequisitesError(null);
    identityCtl.close();
    form.reset();
    close();
  };

  return (
    <>
      {variant === 'button' ? (
        <Button variant="light" leftSection={<IconFileText size={16} />} onClick={(e) => { e.stopPropagation(); open(); }}>
          {t('contractGen.create')}
        </Button>
      ) : (
        <Tooltip label={t('contractGen.tooltip')}>
          <ActionIcon variant="subtle" color="brand" onClick={(e) => { e.stopPropagation(); open(); }} aria-label="contract">
            <IconFileText size={18} />
          </ActionIcon>
        </Tooltip>
      )}

      <Modal opened={opened} onClose={closeAll} title={t('contractGen.modalTitle')} size="lg" onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <Center h={200}><Loader /></Center>
        ) : result ? (
          <Stack>
            <Alert color={result.notified ? 'green' : 'yellow'} icon={<IconCheck size={18} />} title={t('contractGen.linkReady')}>
              {t('contractGen.numberIssued', { number: result.contractNumber })}
              <br />
              {result.notified ? t('contractGen.notified') : t('contractGen.notNotified')}
              <Group mt="sm" gap="xs" wrap="nowrap">
                <Code style={{ flex: 1, wordBreak: 'break-all' }}>{result.link}</Code>
                <CopyButton value={result.link}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant="light" color={copied ? 'teal' : 'brand'} leftSection={<IconCopy size={14} />} onClick={copy}>
                      {copied ? t('contractGen.copied') : t('contractGen.copy')}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Alert>
            {result.phoneAttached === 'ok' && (
              <Alert color="green" variant="light" py="xs">{t('contractGen.phoneAttached')}</Alert>
            )}
            {result.phoneAttached === 'failed' && (
              <Alert color="orange" variant="light" py="xs" icon={<IconAlertTriangle size={18} />}>
                {t('contractGen.phoneAttachFailed')}
              </Alert>
            )}
            <Alert color="blue" variant="light">{t('contractGen.linkNote')}</Alert>
            {/* Yaratilgan shartnoma `id`si endi ishlatiladi: xodim uni ro'yxatdan
                qo'lda qidirmasdan, to'g'ridan-to'g'ri kartochkasini ochadi. */}
            <Group justify="space-between">
              <Anchor component={Link} to={`/contracts?id=${encodeURIComponent(result.id)}`} onClick={closeAll} size="sm">
                <Group gap={4} component="span"><IconFileText size={14} />{t('contractGen.openContract')}</Group>
              </Anchor>
              <Button onClick={closeAll}>{t('contractGen.close')}</Button>
            </Group>
          </Stack>
        ) : (
          <form
            onSubmit={form.onSubmit(
              (v) => sendMut.mutate(v),
              // Yopiq bo'limdagi xato KO'RINMAY qolmasin: forma jimgina yuborilmay
              // turgandek tuyulardi. Xato bo'lsa bo'lim avtomatik ochiladi.
              (errors) => {
                if (errors.parentAddress || errors.passport || errors.jshshir) identityCtl.open();
              }
            )}
          >
            <Stack>
              {/* Rekvizitlarsiz shartnoma yaratib bo'lmaydi. Rekvizitlarni FAQAT STAFF
                  (direktor/admin) tahrirlaydi — RECEPTION/ACCOUNTANT Sozlamalar sahifasini
                  ochsa ham bu bo'limni ko'rmaydi, shuning uchun ularga havola o'rniga
                  "kimga murojaat qilish" aytiladi (avval boshi berk ko'cha edi). */}
              {requisitesError && (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('contractGen.requisitesTitle')}>
                  <Stack gap="xs">
                    <span>{requisitesError}</span>
                    {canFixRequisites ? (
                      <>
                        <span>{t('contractGen.requisitesHint')}</span>
                        <Anchor component={Link} to="/settings" onClick={closeAll} size="sm">
                          <Group gap={4} component="span"><IconSettings size={14} />{t('contractGen.requisitesLink')}</Group>
                        </Anchor>
                      </>
                    ) : (
                      <span>{t('contractGen.requisitesHintNoAccess')}</span>
                    )}
                  </Stack>
                </Alert>
              )}

              <Group grow>
                <TextInput
                  label={t('contractGen.contractNumber')}
                  description={t('contractGen.contractNumberAuto')}
                  placeholder={t('contractGen.contractNumberPlaceholder')}
                  {...form.getInputProps('contractNumber')}
                />
                <DateInput label={t('contractGen.contractDate')} valueFormat="DD.MM.YYYY" {...form.getInputProps('date')} />
              </Group>
              <TextInput label={t('contractGen.studentName')} {...form.getInputProps('studentFullName')} />
              <TextInput
                label={t('contractGen.parentName')}
                description={t('contractGen.parentNameOptional')}
                {...form.getInputProps('parentFullName')}
              />
              <Group grow>
                {/* `min` ATAYLAB 0: Mantine `clampBehavior='blur'` bilan min=1 bo'lsa
                    to'ldirilmagan maydon fokusdan chiqqanda JIMGINA 1 so'mga aylanardi.
                    Musbatlikni forma validatsiyasi (server qoidasi bilan bir xil) tekshiradi. */}
                <NumberInput
                  label={t('studentDetailPage.monthlyAmount')}
                  thousandSeparator=" "
                  min={0}
                  max={MAX_MONTHLY_PAYMENT}
                  allowNegative={false}
                  allowDecimal={false}
                  required
                  {...form.getInputProps('monthlyPayment')}
                />
                <NumberInput
                  label={t('contractGen.durationMonths')}
                  min={1}
                  max={12}
                  allowNegative={false}
                  allowDecimal={false}
                  required
                  {...form.getInputProps('monthsCount')}
                />
              </Group>
              {knownPhones.length > 0 && (
                <Select
                  label={t('contractGen.selectFromPhones')}
                  placeholder={t('contractGen.selectPhone')}
                  data={knownPhones.map((pp) => ({ value: pp.number, label: `${pp.label}: ${pp.number}${pp.isPrimary ? ` (${t('contactsMgr.primary')})` : ''}` }))}
                  value={knownPhones.some((pp) => pp.number === form.values.parentPhone) ? form.values.parentPhone : null}
                  onChange={(v) => v && form.setFieldValue('parentPhone', v)}
                  comboboxProps={{ withinPortal: true }}
                />
              )}
              <PhoneInput label={t('contractGen.phoneLabel')} required {...form.getInputProps('parentPhone')} />
              {isNewPhone && (
                <Checkbox
                  label={t('contractGen.attachPhone')}
                  description={t('contractGen.attachPhoneHint')}
                  {...form.getInputProps('attachPhone', { type: 'checkbox' })}
                />
              )}
              <Group grow>
                <DateInput label={t('contractGen.fromDate')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('fromDate')} />
                <DateInput label={t('contractGen.toDate')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('toDate')} />
              </Group>

              {/* T1: shaxsni tasdiqlovchi maydonlar — YOPIQ va IXTIYORIY.
                  Maktabga kerak bo'lgani: o'quvchi, sana/muddat, oylik to'lov, oylar
                  soni va telefon. Qolgani ota-onaning ishi. */}
              <Alert color="blue" variant="light" py="xs" icon={<IconInfoCircle size={18} />}>
                {t('contractGen.identityHint')}
              </Alert>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={identityCtl.toggle}
                rightSection={identityOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                styles={{ root: { alignSelf: 'flex-start' } }}
              >
                <Group gap={6} component="span">
                  <span>{t('contractGen.identitySection')}</span>
                  {identityFilled > 0 && (
                    <Badge size="sm" variant="light" color="brand">{t('contractGen.identityFilled', { count: identityFilled })}</Badge>
                  )}
                </Group>
              </Button>
              <Collapse in={identityOpen}>
                <Stack gap="sm">
                  <Textarea
                    label={t('contractGen.parentAddress')}
                    description={t('contractGen.parentFillsHint')}
                    autosize
                    minRows={2}
                    {...form.getInputProps('parentAddress')}
                  />
                  <Group grow>
                    <TextInput
                      label={t('contractGen.passport')}
                      placeholder="AB 1234567"
                      description={t('contractGen.parentFillsHint')}
                      {...form.getInputProps('passport')}
                      onChange={(e) => form.setFieldValue('passport', formatPassport(e.currentTarget.value))}
                    />
                    <TextInput
                      label={t('contractGen.jshshir')}
                      placeholder={t('contractGen.jshshirPlaceholder')}
                      description={t('contractGen.parentFillsHint')}
                      inputMode="numeric"
                      {...form.getInputProps('jshshir')}
                      onChange={(e) => form.setFieldValue('jshshir', e.currentTarget.value.replace(/\D/g, '').slice(0, 14))}
                    />
                  </Group>
                </Stack>
              </Collapse>

              {/* Ota-ona akkaunti yo'q — bildirishnoma bormasligini OLDINDAN aytamiz
                  (aniq natija baribir server javobidagi `notified` dan). */}
              {s && !s.parent?.id && (
                <Alert color="yellow" variant="light" py="xs" icon={<IconAlertTriangle size={18} />}>
                  {t('contractGen.noParentWarn')}
                </Alert>
              )}
              <Group justify="flex-end" mt="sm">
                <Button variant="default" onClick={closeAll}>{t('common.cancel')}</Button>
                <Button type="submit" loading={sendMut.isPending} leftSection={<IconSend size={18} />}>{t('contractGen.createAndSend')}</Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>
    </>
  );
}
